/**
 * CreateCollectionOfferPopover - Dialog for creating collection-wide buy offers
 *
 * Supports optional trait filtering to target specific NFTs within a collection.
 * Uses Dialog instead of Popover for more space when traits are available.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as SubframeCore from '@subframe/core';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Select } from '../../ui/components/Select';
import { Badge } from '../../ui/components/Badge';
import { Switch } from '../../ui/components/Switch';
import {
  createCollectionOffer,
  activateOffer,
  calculateOfferFees,
  type ExpirationOption,
  type CreateOfferAmounts,
  type TraitFilters,
} from '../../api/collectionOffers';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { getRarityInfoInCategory } from '../../utils/formatters';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import type { SigningPayload } from '../../types/signing';

// Trait stats with percentage data for rarity coloring
interface TraitValueStats {
  count: number;
  percentage?: number;
}

interface TraitStats {
  [traitType: string]: {
    [value: string]: TraitValueStats;
  };
}

interface CreateCollectionOfferPopoverProps {
  collectionId: string;
  collectionName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  children: React.ReactNode; // The trigger button
  availableTraits?: {
    [traitType: string]: string[];
  };
  traitStats?: TraitStats; // Full trait stats for rarity coloring
}

const EXPIRATION_OPTIONS: { value: ExpirationOption; label: string }[] = [
  { value: '1_day', label: '1 day' },
  { value: '3_days', label: '3 days' },
  { value: '7_days', label: '7 days' },
  { value: '30_days', label: '30 days' },
  { value: 'no_expiration', label: 'No expiration' },
];

export const CreateCollectionOfferPopover: React.FC<CreateCollectionOfferPopoverProps> = ({
  collectionId,
  collectionName,
  open,
  onOpenChange,
  onSuccess,
  children,
  availableTraits = {},
  traitStats,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  // Form state
  const [offerAmount, setOfferAmount] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [expiration, setExpiration] = useState<ExpirationOption>('7_days');
  const [ladderEnabled, setLadderEnabled] = useState<boolean>(false);
  const [ladderStep, setLadderStep] = useState<string>('');
  const [selectedTraits, setSelectedTraits] = useState<TraitFilters>({});
  const [showTraitFilters, setShowTraitFilters] = useState(false);
  const [expandedTraitTypes, setExpandedTraitTypes] = useState<Set<string>>(new Set());

  // Calculated fees
  const [fees, setFees] = useState<CreateOfferAmounts | null>(null);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  // Normalized signing payload (any provider). When set, WalletSigningModal owns the
  // signing lifecycle (Xaman QR / deep link, or WalletConnect approval for joey-wc).
  const [signingPayload, setSigningPayload] = useState<SigningPayload | null>(null);
  const [offerId, setOfferId] = useState<string>('');

  // Batch signing state
  const [currentOfferIndex, setCurrentOfferIndex] = useState<number>(0);
  const [completedOffers, setCompletedOffers] = useState<string[]>([]);
  const hasReachedFinalStateRef = React.useRef<boolean>(false);

  // Calculate fees when amount changes
  useEffect(() => {
    const amount = parseFloat(offerAmount);
    if (!isNaN(amount) && amount > 0) {
      setFees(calculateOfferFees(amount));
    } else {
      setFees(null);
    }
  }, [offerAmount]);

  // Reset form when popover opens
  useEffect(() => {
    if (open) {
      setOfferAmount('');
      setQuantity(1);
      setExpiration('7_days');
      setLadderEnabled(false);
      setLadderStep('');
      setSelectedTraits({});
      setShowTraitFilters(false);
      setExpandedTraitTypes(new Set());
      setFees(null);
      setProcessing(false);
      setError('');
      setSigningPayload(null);
      setOfferId('');
      setCurrentOfferIndex(0);
      setCompletedOffers([]);
      // Reset refs for new session
      hasReachedFinalStateRef.current = false;
    }
  }, [open]);

  // Build trait filters object (only include traits with selections)
  const buildTraitFilters = (): TraitFilters | null => {
    const filters: TraitFilters = {};
    Object.entries(selectedTraits).forEach(([traitType, values]) => {
      if (values.length > 0) {
        filters[traitType] = values;
      }
    });
    return Object.keys(filters).length > 0 ? filters : null;
  };

  // Trait toggle handler
  const handleTraitToggle = (traitType: string, value: string) => {
    setSelectedTraits(prev => {
      const currentValues = prev[traitType] || [];
      const isSelected = currentValues.includes(value);

      if (isSelected) {
        // Remove value
        const newValues = currentValues.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [traitType]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [traitType]: newValues };
      } else {
        // Add value
        return { ...prev, [traitType]: [...currentValues, value] };
      }
    });
  };

  const isTraitSelected = (traitType: string, value: string): boolean => {
    return selectedTraits[traitType]?.includes(value) || false;
  };

  const selectedTraitCount = Object.values(selectedTraits).reduce(
    (total, values) => total + values.length,
    0
  );

  // Check if ladder configuration is invalid (would result in offers below minimum)
  const isLadderInvalid = useMemo(() => {
    if (!ladderEnabled || quantity <= 1) return false;
    const amount = parseFloat(offerAmount) || 0;
    const step = parseFloat(ladderStep) || 0;
    if (step <= 0) return false;
    const lowestOffer = amount - ((quantity - 1) * step);
    return lowestOffer < 1;
  }, [ladderEnabled, quantity, offerAmount, ladderStep]);

  // Use refs to avoid stale closures in batch-signing callbacks.
  // IMPORTANT: These refs must be declared before functions that use them
  const quantityRef = React.useRef(quantity);
  const currentOfferIndexRef = React.useRef(currentOfferIndex);
  const completedOffersRef = React.useRef(completedOffers);
  const offerIdRef = React.useRef(offerId);
  const showSuccessRef = React.useRef(showSuccess);
  const onOpenChangeRef = React.useRef(onOpenChange);
  const onSuccessRef = React.useRef(onSuccess);
  const ladderEnabledRef = React.useRef(ladderEnabled);
  const ladderStepRef = React.useRef(0);
  const baseAmountRef = React.useRef(0);

  // Keep refs in sync
  useEffect(() => { quantityRef.current = quantity; }, [quantity]);
  useEffect(() => { currentOfferIndexRef.current = currentOfferIndex; }, [currentOfferIndex]);
  useEffect(() => { ladderEnabledRef.current = ladderEnabled; }, [ladderEnabled]);
  useEffect(() => { ladderStepRef.current = parseFloat(ladderStep) || 0; }, [ladderStep]);
  useEffect(() => { completedOffersRef.current = completedOffers; }, [completedOffers]);
  useEffect(() => { offerIdRef.current = offerId; }, [offerId]);
  useEffect(() => { showSuccessRef.current = showSuccess; }, [showSuccess]);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; }, [onOpenChange]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  // Calculate the offer amount for a specific index in the ladder
  const getOfferAmountForIndex = (index: number): number => {
    const base = baseAmountRef.current;
    if (!ladderEnabledRef.current) {
      return base;
    }
    const step = ladderStepRef.current;
    return base - (index * step);
  };

  // Helper to create the next offer in batch
  const createNextOffer = async () => {
    if (!user?.xrpl_address) return;

    const currentIndex = currentOfferIndexRef.current;
    const amount = getOfferAmountForIndex(currentIndex);

    try {
      const result = await createCollectionOffer({
        collectionId,
        offerAmountXrp: amount,
        expiration,
        userAddress: user.xrpl_address,
        traitFilters: buildTraitFilters(),
      });

      setOfferId(result.offerId);
      setSigningPayload(result.signing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create offer');
      setSigningPayload(null);
    }
  };

  // WalletSigningModal resolved the signing in our favor. Activates the escrow-backed
  // offer, then advances to the next offer in the batch (or completes).
  const handleSignedSuccess = useCallback(async (escrowCreateTransactionHash?: string) => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;

    try {
      const currentOfferId = offerIdRef.current;
      await activateOffer({
        offerId: currentOfferId,
        escrowCreateTransactionHash,
      });
      const newCompleted = [...completedOffersRef.current, currentOfferId];
      setCompletedOffers(newCompleted);
      completedOffersRef.current = newCompleted;

      // Check if we have more offers to sign
      const nextIndex = currentOfferIndexRef.current + 1;
      if (nextIndex < quantityRef.current) {
        // More offers to sign - reset final state flag for next offer
        hasReachedFinalStateRef.current = false;
        setCurrentOfferIndex(nextIndex);
        currentOfferIndexRef.current = nextIndex;
        // Clear the signed payload while the next one is created
        setSigningPayload(null);
        // Small delay before creating next offer
        setTimeout(() => {
          createNextOffer();
        }, 500);
      } else {
        // All offers completed
        const qty = quantityRef.current;
        setSigningPayload(null);
        showSuccessRef.current(`${qty} collection offer${qty > 1 ? 's' : ''} created successfully!`);
        onOpenChangeRef.current(false);
        onSuccessRef.current?.();
      }
    } catch (err) {
      setError('Offer created but failed to activate. Please contact support.');
      setSigningPayload(null);
    }
  }, []);

  // WalletSigningModal reported a rejection.
  const handleRejected = useCallback(() => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;

    if (completedOffersRef.current.length > 0) {
      setError(`Transaction rejected. ${completedOffersRef.current.length} of ${quantityRef.current} offers were created.`);
    } else {
      setError('Transaction was rejected');
    }
    setSigningPayload(null);
  }, []);

  // WalletSigningModal reported an expiration.
  const handleExpired = useCallback(() => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;

    if (completedOffersRef.current.length > 0) {
      setError(`Transaction expired. ${completedOffersRef.current.length} of ${quantityRef.current} offers were created.`);
    } else {
      setError('Transaction request expired');
    }
    setSigningPayload(null);
  }, []);

  const handleSubmit = async () => {
    setError('');

    if (!user?.xrpl_address) {
      setError('Please connect your wallet first');
      return;
    }

    const amount = parseFloat(offerAmount);
    if (isNaN(amount) || amount < 1) {
      setError('Minimum offer is 1 XRP');
      return;
    }

    if (quantity < 1 || quantity > 5) {
      setError('Quantity must be between 1 and 5');
      return;
    }

    // Validate ladder settings if enabled
    if (ladderEnabled && quantity > 1) {
      const step = parseFloat(ladderStep);
      if (isNaN(step) || step < 1 || step > 1000) {
        setError('Ladder step must be between 1 and 1000 XRP');
        return;
      }
      const lowestOffer = amount - ((quantity - 1) * step);
      if (lowestOffer < 1) {
        setError(`Lowest offer would be ${lowestOffer.toFixed(2)} XRP. Reduce quantity or step.`);
        return;
      }
    }

    // Set the base amount ref for the ladder calculation
    baseAmountRef.current = amount;

    setProcessing(true);
    setCurrentOfferIndex(0);
    setCompletedOffers([]);
    hasReachedFinalStateRef.current = false;

    try {
      // Create the first offer (always at the base amount)
      const result = await createCollectionOffer({
        collectionId,
        offerAmountXrp: amount,
        expiration,
        userAddress: user.xrpl_address,
        traitFilters: buildTraitFilters(),
      });

      setOfferId(result.offerId);
      setSigningPayload(result.signing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    if (signingPayload) {
      // Warn user about abandoning signing
      const msg = completedOffers.length > 0
        ? `Are you sure you want to cancel? ${completedOffers.length} of ${quantity} offers have been created.`
        : 'Are you sure you want to cancel? The offer will not be created.';
      if (!confirm(msg)) {
        return;
      }
    }
    setOfferAmount('');
    setQuantity(1);
    setExpiration('7_days');
    setLadderEnabled(false);
    setLadderStep('');
    setSelectedTraits({});
    setShowTraitFilters(false);
    setError('');
    setSigningPayload(null);
    setCurrentOfferIndex(0);
    setCompletedOffers([]);
    onOpenChange(false);
  };

  const hasTraits = Object.keys(availableTraits).length > 0;

  // Get rarity-based styling for a trait value
  const getTraitStyle = (traitType: string, value: string, isSelected: boolean) => {
    // Get percentage from traitStats if available
    const percentage = traitStats?.[traitType]?.[value]?.percentage;
    const allPercentages = traitStats?.[traitType] ? Object.values(traitStats[traitType]).map(v => v.percentage).filter((p): p is number => p != null) : [];
    const rarityInfo = getRarityInfoInCategory(percentage, allPercentages);

    if (isSelected) {
      // Selected state uses white background with dark text for clear contrast
      return {
        backgroundColor: 'white',
        color: '#1e293b',
        borderColor: 'white',
      };
    }

    // Legendary traits - only set border, let CSS class handle text shimmer
    if (rarityInfo.tier === 'legendary') {
      return {
        borderColor: '#f59e0b',
      };
    }

    // Other rarities use their color with subtle background
    return {
      backgroundColor: `${rarityInfo.color}15`, // 15 = ~9% opacity in hex
      color: rarityInfo.color,
      borderColor: rarityInfo.color,
    };
  };

  const formatXrp = (amount: number): string => {
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  return (
    <>
      {/* Trigger button */}
      <div onClick={() => onOpenChange(true)}>
        {children}
      </div>

      {/* Dialog for creating offers */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className={`max-h-[90vh] overflow-y-auto ${hasTraits ? 'w-full max-w-[600px]' : 'w-full max-w-[400px]'}`}>
          <div className="flex w-full flex-col items-start gap-4 p-6">
            <>
                {/* Header */}
                <div className="flex w-full items-start justify-between">
                  <div className="flex flex-col gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Make Collection Offer
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {selectedTraitCount > 0
                        ? `Offer to buy NFTs with specific traits in ${collectionName}`
                        : `Offer to buy ANY NFT in ${collectionName}`}
                    </span>
                  </div>
                </div>

                {/* Offer Amount */}
                <TextField
                  className="h-auto w-full flex-none"
                  label="Offer Amount (XRP)"
                  helpText={fees ? `You'll escrow ${formatXrp(fees.escrowAmountXrp * quantity)} XRP total (includes 1% fee)` : ''}
                  error={!!error && error.includes('Minimum')}
                >
                  <TextField.Input
                    type="number"
                    step="0.000001"
                    min="1"
                    placeholder="Enter amount"
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    disabled={processing}
                  />
                </TextField>

                {/* Quantity and Expiration Row */}
                <div className="flex w-full gap-2">
                  <Select
                    className="w-24"
                    label="Quantity"
                    value={quantity.toString()}
                    onValueChange={(value) => setQuantity(parseInt(value))}
                    disabled={processing}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <Select.Item key={num} value={num.toString()}>
                        {num}
                      </Select.Item>
                    ))}
                  </Select>

                  <Select
                    className="flex-1"
                    label="Expiration"
                    value={expiration}
                    onValueChange={(value) => setExpiration(value as ExpirationOption)}
                    disabled={processing}
                  >
                    {EXPIRATION_OPTIONS.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select>
                </div>

                {/* Trait Filters Section */}
                {hasTraits && (
                  <div className="w-full flex flex-col gap-3 border-t border-neutral-border pt-4">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setShowTraitFilters(!showTraitFilters)}
                        className="flex items-center gap-2 text-body-bold font-body-bold text-default-font hover:text-brand-600 transition-colors"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${showTraitFilters ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        Filter by Traits (Optional)
                      </button>
                      {selectedTraitCount > 0 && (
                        <Badge variant="brand">
                          {selectedTraitCount} selected
                        </Badge>
                      )}
                    </div>

                    {showTraitFilters && (
                      <div className="flex flex-col gap-4 max-h-[250px] overflow-y-auto rounded-md p-3">
                        {Object.entries(availableTraits).map(([traitType, values]) => {
                          // Sort values by rarity (rarest first)
                          const sortedValues = [...values].sort((a, b) => {
                            const percentA = traitStats?.[traitType]?.[a]?.percentage ?? 100;
                            const percentB = traitStats?.[traitType]?.[b]?.percentage ?? 100;
                            return percentA - percentB;
                          });

                          const isExpanded = expandedTraitTypes.has(traitType);
                          const displayLimit = 15;
                          const valuesToShow = isExpanded ? sortedValues : sortedValues.slice(0, displayLimit);
                          const hiddenCount = sortedValues.length - displayLimit;

                          return (
                            <div key={traitType} className="flex flex-col gap-2">
                              <span className="text-caption-bold font-caption-bold text-default-font">
                                {traitType}
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                {valuesToShow.map((value) => {
                                  const isSelected = isTraitSelected(traitType, value);
                                  const style = getTraitStyle(traitType, value, isSelected);
                                  const percentage = traitStats?.[traitType]?.[value]?.percentage;
                                  const isLegendary = !isSelected && percentage !== undefined && percentage < 1;
                                  return (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={() => handleTraitToggle(traitType, value)}
                                      className="px-2.5 py-1 rounded-full text-caption font-caption transition-all border hover:opacity-80 flex items-center gap-1"
                                      style={style}
                                    >
                                      {isSelected && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                      <span className={isLegendary ? 'legendary-shimmer' : ''}>
                                        {value}
                                        {percentage !== undefined && (
                                          <span className={isLegendary ? '' : 'ml-1 opacity-70'}>
                                            {' '}{percentage < 1 ? percentage.toFixed(1) : Math.round(percentage)}%
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                  );
                                })}
                                {!isExpanded && hiddenCount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedTraitTypes(prev => new Set([...prev, traitType]))}
                                    className="px-2 py-1 text-caption font-caption text-brand-600 hover:text-brand-700 hover:underline cursor-pointer"
                                  >
                                    +{hiddenCount} more
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedTraitCount > 0 && !showTraitFilters && (
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(selectedTraits).map(([traitType, values]) =>
                          values.map(value => (
                            <Badge key={`${traitType}-${value}`} variant="neutral" className="text-caption">
                              {traitType}: {value}
                            </Badge>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Ladder Options - only show when quantity > 1 */}
                {quantity > 1 && (
                  <div className="flex w-full flex-col gap-3 border-t border-neutral-border pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-body font-body text-default-font">
                          Ladder offers
                        </span>
                        <span className="text-caption text-subtext-color">
                          Decrease each offer by a fixed amount
                        </span>
                      </div>
                      <Switch
                        checked={ladderEnabled}
                        onCheckedChange={setLadderEnabled}
                        disabled={processing}
                      />
                    </div>

                    {ladderEnabled && (
                      <TextField
                        className="w-full"
                        label="Step amount (XRP)"
                        helpText={(() => {
                          const base = parseFloat(offerAmount) || 0;
                          const step = parseFloat(ladderStep) || 0;
                          if (step > 0 && base > 0) {
                            const amounts = Array.from({ length: quantity }, (_, i) => base - (i * step));
                            const lowestOffer = amounts[amounts.length - 1];
                            if (lowestOffer < 1) {
                              return undefined; // Error shown separately
                            }
                            return `Offers: ${amounts.map(a => formatXrp(a)).join(', ')} XRP`;
                          }
                          return `Each offer will be this much less than the previous`;
                        })()}
                        error={isLadderInvalid}
                      >
                        <TextField.Input
                          type="number"
                          step="1"
                          min="1"
                          max="1000"
                          placeholder="e.g. 5"
                          value={ladderStep}
                          onChange={(e) => setLadderStep(e.target.value)}
                          disabled={processing}
                        />
                      </TextField>
                    )}

                    {/* Error message for invalid ladder */}
                    {isLadderInvalid && (
                      <span className="text-caption text-error-600">
                        Lowest offer would be {(parseFloat(offerAmount) - ((quantity - 1) * parseFloat(ladderStep))).toFixed(2)} XRP. Minimum is 1 XRP.
                      </span>
                    )}

                    {/* Summary for non-ladder mode */}
                    {!ladderEnabled && (
                      <span className="text-caption text-subtext-color">
                        {quantity} offers at {offerAmount || '0'} XRP each
                      </span>
                    )}
                  </div>
                )}

                {fees && (
                  <div className="flex w-full flex-col gap-1 text-caption text-subtext-color border-t border-neutral-border pt-3">
                    {/* Show seller receives amounts */}
                    <div className="flex justify-between">
                      <span>{quantity > 1 ? 'Sellers will receive' : 'Seller receives'}</span>
                      <span className="text-default-font">
                        {(() => {
                          if (ladderEnabled && quantity > 1 && parseFloat(ladderStep) > 0) {
                            const step = parseFloat(ladderStep);
                            const sellerAmounts = Array.from({ length: quantity }, (_, i) =>
                              (fees.displayAmountXrp - (i * step)) * 0.99
                            );
                            return `${sellerAmounts.map(a => formatXrp(a)).join(', ')} XRP`;
                          }
                          return `${formatXrp(fees.sellerReceivesXrp)} XRP`;
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{quantity > 1 ? `Total to escrow (${quantity} offers)` : 'Total to escrow'}</span>
                      <span className="text-default-font font-medium">
                        {(() => {
                          const base = fees.displayAmountXrp;
                          const step = ladderEnabled ? (parseFloat(ladderStep) || 0) : 0;
                          let totalEscrow = 0;
                          for (let i = 0; i < quantity; i++) {
                            const offerAmt = base - (i * step);
                            totalEscrow += offerAmt * 1.01;
                          }
                          return formatXrp(totalEscrow);
                        })()} XRP
                      </span>
                    </div>
                  </div>
                )}

                {error && (
                  <span className="text-caption font-caption text-error-600">
                    {error}
                  </span>
                )}

                <div className="flex w-full items-center gap-2 pt-2">
                  <Button
                    className="h-10 w-auto flex-none grow"
                    variant="neutral-secondary"
                    size="large"
                    onClick={handleCancel}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="h-10 w-auto flex-none grow"
                    size="large"
                    onClick={handleSubmit}
                    disabled={processing || !fees || isLadderInvalid}
                  >
                    {processing ? 'Creating...' : 'Create Offer'}
                  </Button>
                </div>
              </>
          </div>
        </Dialog.Content>
      </Dialog>

      {signingPayload && (
        <WalletSigningModal
          open={true}
          onClose={() => setSigningPayload(null)}
          title={quantity > 1 ? `Sign Transaction ${currentOfferIndex + 1} of ${quantity}` : 'Sign Transaction'}
          payload={signingPayload}
          onSuccess={(txid) => handleSignedSuccess(txid)}
          onRejected={handleRejected}
          onExpired={handleExpired}
        />
      )}
    </>
  );
};
